import { ref } from "vue";
import { defineStore } from "pinia";
import api from "@/api";
import type {
    AttributeLogAttribution,
    AttributeLogBit,
    AttributeLogChange,
    AttributeLogField,
    AttributeLogItem,
    UserDetail,
} from "@/api";
import { newestChange, type LogStatus, type LogTarget } from "@/attributeLog";
import { SPECIAL_USER_NAMES } from "@/config";
import { useOverviewStore } from "@/stores/overview";

export interface AttributeLogEntry {
    status: LogStatus;
    changes: AttributeLogChange[];
    total: number;
    limit: number;
    // Keyed by the bit as a string: a bigint cannot index an object.
    bits: Record<string, AttributeLogBit>;
    items: Record<string, AttributeLogItem>;
    fields: Record<string, AttributeLogField>;
}

// The summaries are built from the whole log regardless of the limit, but the
// newest changes may be ones the tooltip has nothing to say about, so even the
// summary reads a page rather than one change.
const SUMMARY_LIMIT = 10;
const HISTORY_LIMIT = 50;

const emptyEntry = (status: LogStatus): AttributeLogEntry => ({
    status,
    changes: [],
    total: 0,
    limit: 0,
    bits: {},
    items: {},
    fields: {},
});

// A page holding the whole log answers any limit, however small it was read at.
const covers = (entry: AttributeLogEntry | undefined, limit: number): boolean =>
    entry?.status === "loaded" &&
    (entry.limit >= limit || entry.total <= entry.changes.length);

// The log points at a user by id alone, apart from the actor it names. The
// loaded user carries the names we can answer with: themselves, their owner,
// and the lineages, which hold the users an inherited_from points at.
const namesOf = (user: UserDetail): Record<number, string> => {
    const names: Record<number, string> = { [user.id_user]: user.username };

    if (user.owner?.username) {
        names[user.owner.id_user] = user.owner.username;
    }

    for (const attribute of Object.values(user.attributes ?? {})) {
        for (const lineage of attribute.inherit_lineage) {
            names[lineage.id_user] = lineage.username;
        }

        for (const lineage of attribute.whocanset_lineage ?? []) {
            names[lineage.id_user] = lineage.username;
        }
    }

    return names;
};

// A read cache, so it holds none of the save/reset machinery the section stores
// have. It still needs updateUser: a stale entry would name the wrong person.
export const useAttributeLogStore = defineStore("attributeLog", () => {
    const idUser = ref<number | undefined>(undefined);
    const entries = ref<Record<string, AttributeLogEntry>>({});
    const names = ref<Record<number, string>>({});

    // The limit is kept so a caller wanting more than the flight will bring
    // asks for the rest rather than awaiting a short page.
    const inFlight: Record<string, { limit: number; done: Promise<void> }> = {};

    const updateUser = (user: UserDetail) => {
        idUser.value = user.id_user;
        entries.value = {};
        names.value = namesOf(user);
    };

    const nameOf = (id: number): string =>
        names.value[id] ?? SPECIAL_USER_NAMES[id] ?? `ID:${id}`;

    const invalidate = (attributes: string[]) => {
        for (const name of attributes) {
            delete entries.value[name];
            delete inFlight[name];
        }
    };

    // The whole attribute, not the bitmask the API could narrow it to: a bit is
    // filtered out of the page here anyway, and inheriting the attribute is a
    // change its bits have to report too.
    const fetchInto = async (
        requestedFor: number,
        name: string,
        limit: number
    ) => {
        try {
            const log = await api.users.attributeLog(requestedFor, [name], {
                limit,
            });

            // The same race the section stores guard `save` against.
            if (requestedFor !== useOverviewStore().idUser) {
                return;
            }

            const entry = log.attributes[name];

            entries.value[name] = {
                status: "loaded",
                changes: entry?.changes ?? [],
                total: entry?.total_changes ?? 0,
                limit,
                bits: Object.fromEntries(
                    (entry?.bits ?? []).map((bit) => [bit.bit.toString(), bit])
                ),
                items: Object.fromEntries(
                    (entry?.items ?? []).map((item) => [item.item, item])
                ),
                fields: entry?.fields ?? {},
            };
        } catch (error) {
            console.error("Could not load the attribute log", name, error);

            if (requestedFor === useOverviewStore().idUser) {
                entries.value[name] = emptyEntry("error");
            }
        } finally {
            if (inFlight[name]?.limit === limit) {
                delete inFlight[name];
            }
        }
    };

    const request = (name: string, limit = SUMMARY_LIMIT): Promise<void> => {
        const requestedFor = idUser.value;
        const pending = inFlight[name];

        if (pending && pending.limit >= limit) {
            return pending.done;
        }

        if (requestedFor === undefined || covers(entries.value[name], limit)) {
            return Promise.resolve();
        }

        // Reading a bigger page leaves the entry standing, so a summary already
        // on screen does not fall back to "Loading".
        if (!entries.value[name]) {
            entries.value[name] = emptyEntry("loading");
        }

        inFlight[name] = { limit, done: fetchInto(requestedFor, name, limit) };

        return inFlight[name].done;
    };

    const requestHistory = (name: string): Promise<void> =>
        request(name, HISTORY_LIMIT);

    const entryOf = (name: string): AttributeLogEntry | undefined =>
        entries.value[name];

    const hasHistory = (name: string): boolean =>
        covers(entries.value[name], HISTORY_LIMIT);

    // A value has no summary of its own: it is the newest change with something
    // to show, since the log holds changes that moved nothing we report.
    const attributionOf = (
        name: string,
        target: LogTarget
    ): AttributeLogAttribution | undefined => {
        const entry = entries.value[name];

        switch (target.scope) {
            case "bit":
                return entry?.bits[target.bit.toString()];
            case "item":
                return entry?.items[target.item];
            case "field":
                return entry?.fields[target.field];
            case "value":
                return newestChange(entry?.changes ?? [], target, {
                    attribute: name,
                });
        }
    };

    return {
        idUser,
        updateUser,
        invalidate,
        request,
        requestHistory,
        entryOf,
        hasHistory,
        attributionOf,
        nameOf,
    };
});
